import Link from "next/link";
import { ArrowRight, Leaf, ShieldCheck, Truck, Users } from "lucide-react";

import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/Card";

const HOMEPAGE_FEATURES = [
  {
    icon: ShieldCheck,
    title: "Trusted Farmers",
    description: "Verified sellers and active farm shops across the network.",
  },
  {
    icon: Truck,
    title: "Fast Local Delivery",
    description: "Order fresh produce from the closest available store.",
  },
  {
    icon: Leaf,
    title: "Fresh Produce",
    description: "From vegetables to grains and livestock products.",
  },
  {
    icon: Users,
    title: "Community First",
    description: "Support local farmers and keep value in your county.",
  },
];

type HomeHeroSectionProps = {
  featuredProducts: Array<{
    _id: string;
    category: string;
    name: string;
    price: number;
  }>;
  products: Array<{
    _id: string;
    category: string;
    name: string;
    price: number;
  }>;
};

export function HomeHeroSection({  }: HomeHeroSectionProps) {

  return (
    <section className="relative overflow-hidden rounded-sm border border-primary/20 bg-gradient-to-br from-primary/10 via-background to-amber-500/10 px-6 py-10 shadow-sm md:px-10 md:py-14">
      <div className="absolute -right-10 top-0 h-56 w-56 rounded-sm bg-primary/20 blur-3xl" />
      <div className="absolute bottom-0 left-0 h-56 w-56 rounded-sm bg-amber-500/20 blur-3xl" />

      <div className="grid gap-10 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
        <div className="space-y-6">
          <Badge variant="brand" className="w-fit rounded-sm px-4 py-1 text-[10px] uppercase tracking-[0.3em]">
            Local farm marketplace
          </Badge>
          <div className="space-y-4">
            <h1 className="max-w-xl text-4xl font-black leading-tight tracking-tight md:text-6xl">
              Fresh produce from trusted local farm shops.
            </h1>
            <p className="max-w-2xl text-base leading-7 text-muted-foreground md:text-lg">
              Shop fresh produce from local farm shops near you.
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <Button asChild className="h-12 rounded-sm bg-emerald-600 px-6 text-white hover:bg-emerald-700">
              <Link href="/shop">
                Shop Now
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button asChild variant="brand" className="h-12 rounded-sm px-6">
              <Link href="/shop/store-list">View Store Locations</Link>
            </Button>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            {[
              { value: "500+", label: "Fresh listings" },
              { value: "24/7", label: "Marketplace access" },
              { value: "Local", label: "Shop support" },
            ].map((stat) => (
              <Card key={stat.label} className="border-primary/20 bg-card/80 shadow-sm">
                <CardContent className="p-4">
                  <div className="text-2xl font-black text-primary">{stat.value}</div>
                  <div className="text-sm text-muted-foreground">{stat.label}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        <div className="grid gap-4">

          <div className="rounded-sm border border-primary/20 bg-card p-4 shadow-sm">
            <div className="grid gap-3 sm:grid-cols-2">
              {HOMEPAGE_FEATURES.map((feature) => {
                const Icon = feature.icon;
                return (
                  <div key={feature.title} className="rounded-sm border border-border bg-muted p-4">
                    <div className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-sm bg-primary/10 text-primary">
                      <Icon className="h-5 w-5" />
                    </div>
                    <h3 className="font-semibold text-foreground">{feature.title}</h3>
                    <p className="mt-1 text-sm leading-6 text-muted-foreground">{feature.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
